import type { LoaderFunctionArgs } from 'react-router-dom'
import { wordpressApi } from '../api/wordpress'

// Server-side preload: set by entry-server.tsx so loaders use the same data
// that gets injected into window.__SSR_DATA__, avoiding double-fetch divergence.
let _preloadedData: Record<string, unknown> | null = null

export function setPreloadedData(data: Record<string, unknown>) {
  _preloadedData = data
}

function consumeSSRData() {
  if (_preloadedData) {
    const data = _preloadedData
    _preloadedData = null
    return data
  }
  if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).__SSR_DATA__) {
    const data = (window as unknown as Record<string, unknown>).__SSR_DATA__
    delete (window as unknown as Record<string, unknown>).__SSR_DATA__
    return data
  }
  return null
}

export async function blogPostLoader({ params }: LoaderFunctionArgs) {
  const ssrData = consumeSSRData()
  if (ssrData && (ssrData as Record<string, unknown>).post) {
    return ssrData
  }

  const { slug } = params
  if (!slug) throw new Response('Not Found', { status: 404 })

  try {
    const [post, categories] = await Promise.all([
      wordpressApi.getPostBySlug(slug),
      wordpressApi.getCategories(),
    ])
    if (!post) throw new Response('Not Found', { status: 404 })
    const recentPosts = await wordpressApi.getRecentPosts(post.id, 3)
    return { post, recentPosts, categories }
  } catch (error) {
    if (error instanceof Response) throw error
    throw new Response('Not Found', { status: 404 })
  }
}

export async function blogListLoader({ params, request }: LoaderFunctionArgs) {
  const ssrData = consumeSSRData()
  if (ssrData && (ssrData as Record<string, unknown>).posts) {
    return ssrData
  }

  const { categorySlug } = params
  const page = parseInt(new URL(request.url).searchParams.get('page') ?? '1', 10)

  try {
    const categories = await wordpressApi.getCategories()
    const categoryId = categorySlug
      ? categories.find(c => c.slug === categorySlug)?.id
      : undefined

    const result = await wordpressApi.getPosts({
      page,
      per_page: 10,
      categories: categoryId ? [categoryId] : undefined,
    })

    return { posts: result.posts, totalPages: result.totalPages, categories, page }
  } catch {
    return { posts: [], totalPages: 1, categories: [], page: 1 }
  }
}
